/**
 * Thai — the default locale. This file is the reference: `en.ts` and `ja.ts` are typed
 * against it, so adding a key here and nowhere else is a type error.
 *
 * Domain nouns use the ordinary Thai terms the industry already uses. Sentences, hints and
 * empty states are written for Broccoli rather than lifted from anywhere.
 */
export const th = {
  common: {
    save: 'บันทึก',
    cancel: 'ยกเลิก',
    close: 'ปิด',
    back: 'ย้อนกลับ',
    apply: 'ตั้งค่า',
    create: 'สร้าง',
    edit: 'แก้ไข',
    delete: 'ลบ',
    duplicate: 'ทำสำเนา',
    search: 'ค้นหา',
    reset: 'รีเซ็ต',
    required: 'จำเป็น',
    optional: 'ไม่บังคับ',
    loading: 'กำลังโหลด…',
    noData: 'ไม่มีข้อมูล',
    confirm: 'ยืนยัน',
    all: 'ทั้งหมด',
    allDay: 'ทั้งวัน',
    from: 'ตั้งแต่',
    to: 'ถึง',
    more: 'เพิ่มเติม',
    selectedCount: 'เลือกแล้ว {count} รายการ',
    unsavedChanges: 'มีการแก้ไขที่ยังไม่บันทึก',
    retry: 'ลองอีกครั้ง',
  },

  nav: {
    createProject: 'สร้างโปรเจกต์',
    projects: 'โปรเจกต์',
    projectBoard: 'โปรเจกต์บอร์ด',
    projectCalendar: 'ปฏิทินโปรเจกต์',
    clients: 'รายชื่อลูกค้า',
    properties: 'รายการทรัพย์สิน',
    import: 'นำเข้า',
    export: 'ส่งออก',
    settings: 'การตั้งค่า',
    members: 'การจัดการสมาชิก',
    plan: 'แพ็กเกจการใช้งาน',
    contact: 'ติดต่อเรา',
    faq: 'คำถามที่พบบ่อย',
    terms: 'ข้อกำหนดการใช้งาน',
    privacy: 'นโยบายความเป็นส่วนตัว',
    collapseSidebar: 'ย่อเมนู',
    expandSidebar: 'ขยายเมนู',
  },

  topbar: {
    approvals: 'การอนุมัติ',
    tasks: 'มอบหมายงาน',
    calendar: 'ปฏิทิน',
    chat: 'แชท',
    notifications: 'การแจ้งเตือน',
    newNotifications: 'มีการแจ้งเตือนใหม่ {count} รายการ',
    help: 'วิธีใช้',
    assistant: 'ผู้ช่วย AI',
    account: 'บัญชีของฉัน',
    profile: 'ข้อมูลส่วนตัว',
    logout: 'ออกจากระบบ',
  },

  auth: {
    loginTitle: 'เข้าสู่ระบบ',
    email: 'อีเมล',
    password: 'รหัสผ่าน',
    passwordHint: 'อย่างน้อย 8 ตัวอักษร',
    continue: 'ดำเนินการต่อ',
    forgotPassword: 'ลืมรหัสผ่าน?',
    externalLogin: 'เข้าสู่ระบบด้วยบัญชีอื่น',
    register: 'สมัครใช้งานใหม่ (ไม่มีค่าใช้จ่าย)',
    troubleLoggingIn: 'เข้าสู่ระบบไม่ได้?',
    showPassword: 'แสดงรหัสผ่าน',
    hidePassword: 'ซ่อนรหัสผ่าน',
    invalidCredentials: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง',
    sessionExpired: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง',
  },

  dashboard: {
    myUnfinished: 'โปรเจกต์ที่รับผิดชอบที่ยังไม่สำเร็จ',
    seeAllProjects: 'ดูโปรเจกต์ทั้งหมด',
    emptyUnfinished: 'ยังไม่มีโปรเจกต์ที่คุณรับผิดชอบค้างอยู่',
  },

  project: {
    // list
    listTitle: 'โปรเจกต์',
    searchPlaceholder: 'โปรเจกต์, ลูกค้า, ที่อยู่',
    advancedSearch: 'ค้นหาโดยละเอียด',
    filterResponsible: 'รับผิดชอบ',
    filterIncomplete: 'ไม่สำเร็จ',
    filterTopLevel: 'โปรเจกต์หลัก',
    displaySettings: 'การตั้งค่าการแสดง',
    displayView: 'มุมมองการแสดงผล',
    emptyList: 'ยังไม่มีโปรเจกต์ในเทมเพลตนี้',
    overdue: 'เกินกำหนด',
    overdueTooltip: 'เลยวันที่เสร็จที่กำหนดไว้',

    // fields
    managementId: 'ID การจัดการโปรเจกต์',
    name: 'ชื่อโปรเจกต์',
    nameHint: 'ชื่อนี้จะแสดงในรายการโปรเจกต์',
    detail: 'รายละเอียด',
    startDate: 'วันที่เริ่ม',
    endDate: 'วันที่เสร็จ',
    status: 'สถานะโปรเจกต์',
    createdAt: 'วันที่สร้างโปรเจกต์',
    createdBy: 'ผู้สร้างโปรเจกต์',
    note: 'หมายเหตุ',
    parentProject: 'โปรเจกต์แม่',
    address: 'ที่อยู่',
    template: 'เทมเพลตโปรเจกต์',
    responsibleCompany: 'บริษัทที่รับผิดชอบ',
    projectMembers: 'สมาชิกโปรเจกต์',
    company: 'บริษัท',
    freeWord: 'คำค้นหา',

    // detail
    createTitle: 'สร้างโปรเจกต์',
    copyProject: 'ทำสำเนาโปรเจกต์',
    downloadCsv: 'ดาวน์โหลดรายละเอียดโปรเจกต์เป็น CSV',
    createQr: 'สร้าง QR Code สำหรับเปิดในแอป',
    yourCompany: 'บริษัทของคุณ',

    tabs: {
      overview: 'ภาพรวม',
      related: 'โปรเจกต์ที่เกี่ยวข้อง',
      gantt: 'แผนภูมิแกนต์',
      tasks: 'มอบหมายงาน',
      workReports: 'การรายงาน',
      photos: 'รูปภาพ',
      documents: 'เอกสาร',
      forms: 'แบบฟอร์ม',
      photoReports: 'รายงานรูปภาพ',
      members: 'สมาชิก',
    },
  },

  displaySettings: {
    arrangeTitle: 'จัดเรียงรายการที่แสดง',
    arrangeHelp: 'ลากเพื่อสลับลำดับคอลัมน์ที่แสดงในตาราง',
    changeItems: 'เลือกรายการที่แสดง',
    changeTitle: 'เลือกรายการที่แสดง',
    changeHelp: 'เลือกคอลัมน์ที่ต้องการให้แสดงในตาราง',
    selectedOf: 'เลือกแล้ว {count}/{max} รายการ',
    limitReached: 'เลือกได้สูงสุด {max} คอลัมน์',
  },

  advancedSearch: {
    title: 'ค้นหาโดยละเอียด',
    addCondition: 'เพิ่มเงื่อนไขการค้นหา',
    removeCondition: 'ลบเงื่อนไข',
  },

  client: {
    listTitle: 'รายชื่อลูกค้า',
    managementId: 'ID การจัดการลูกค้า',
    businessType: 'รูปแบบธุรกิจ',
    fullName: 'ชื่อเต็ม',
    companyName: 'ชื่อบริษัทหรือชื่อทางการค้า',
    personInCharge: 'ผู้รับผิดชอบ',
    address: 'ที่อยู่',
    roomNumber: 'หมายเลขห้อง',
    phone: 'เบอร์โทรศัพท์',
    email: 'อีเมล',
    note: 'หมายเหตุ',
    emptyList: 'ยังไม่มีรายชื่อลูกค้า',
  },

  property: {
    listTitle: 'รายการทรัพย์สิน',
    name: 'ชื่อทรัพย์สิน',
    category: 'ประเภท',
    emptyList: 'ยังไม่มีรายการทรัพย์สิน',
  },

  fieldType: {
    text: 'ข้อความสั้น',
    textarea: 'ข้อความยาว',
    number: 'ตัวเลข',
    date: 'วันที่',
    datetime: 'วันที่และเวลา',
    select: 'ตัวเลือกเดียว',
    multi_select: 'หลายตัวเลือก',
    checkbox_group: 'กล่องกาเครื่องหมาย (หลายข้อ)',
    checkbox: 'กล่องกาเครื่องหมาย',
    user: 'ผู้ใช้',
    company: 'บริษัท',
    relation: 'เชื่อมโยงข้อมูล',
    file: 'ไฟล์แนบ',
    email: 'อีเมล',
    tel: 'เบอร์โทรศัพท์',
    url: 'ลิงก์',
    computed: 'ค่าคำนวณ',
  },

  role: {
    owner: 'เจ้าของบัญชี',
    admin: 'ผู้ดูแลระบบ',
    member: 'พนักงาน',
    partner: 'ผู้รับเหมาภายนอก',
    viewer: 'ดูได้เท่านั้น',
  },

  validation: {
    required: 'กรุณากรอก{field}',
    tooLong: '{field} ยาวเกิน {max} ตัวอักษร',
    invalidEmail: 'รูปแบบอีเมลไม่ถูกต้อง',
    invalidUrl: 'รูปแบบลิงก์ไม่ถูกต้อง',
    invalidNumber: 'กรุณากรอกเป็นตัวเลข',
    endBeforeStart: 'วันที่เสร็จต้องไม่มาก่อนวันที่เริ่ม',
    fileTooLarge: 'ไฟล์ใหญ่เกิน {max}',
    fileTypeNotAllowed: 'ไม่รองรับไฟล์ประเภทนี้',
  },

  error: {
    generic: 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
    notFound: 'ไม่พบข้อมูลที่ต้องการ',
    forbidden: 'คุณไม่มีสิทธิ์เข้าถึงส่วนนี้',
    offline: 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต',
    planLimit: 'เกินขอบเขตของแพ็กเกจปัจจุบัน',
  },
} as const;

/**
 * `th` is `as const` so that message keys resolve to exact dot paths. That also makes every
 * leaf a string *literal* type, which a translation could never satisfy — so widen the
 * leaves back to `string` while keeping the key structure. Other locales are typed against
 * this, which is what makes a drifted locale a compile error.
 */
type WidenLeaves<T> = T extends string ? string : { [K in keyof T]: WidenLeaves<T[K]> };

export type Dictionary = WidenLeaves<typeof th>;
